import { useId, useState, type FormEvent } from 'react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import '../ui/form.css';
import './PasteDialog.css';

/**
 * The way past a bot wall.
 *
 * When a shop blocks the fetch, the page is still perfectly readable in a
 * browser — so the user opens it there, selects all, and pastes it here. The
 * parsers don't care where the markup came from.
 *
 * Ctrl+V of a rendered page carries the text, not the markup, which loses
 * JSON-LD. That's fine and worth saying out loud: the fallbacks still find a
 * title, and the photos can be dropped on the item afterwards.
 */
export function PasteDialog({
  url,
  onClose,
  onPasted,
}: {
  url: string;
  onClose: () => void;
  onPasted: (html: string) => void;
}) {
  const fieldId = useId();
  const [html, setHtml] = useState('');

  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent) {
    event.preventDefault();

    if (html.trim() === '') {
      setError('Paste the page first.');
      return;
    }

    onPasted(html);
  }

  return (
    <Dialog title="Paste The Page" onClose={onClose}>
      <form className="dialog__form" onSubmit={submit}>
        <p className="paste__note">
          {new URL(url).hostname.replace(/^www\./, '')} wouldn’t let us fetch that page. Open it in
          your browser, select everything, and paste it here — or use View Source and paste that,
          which keeps the price and photos.
        </p>

        <div className="field">
          <label className="section-label" htmlFor={fieldId}>
            Page Contents
          </label>
          <textarea
            id={fieldId}
            className="input paste__field"
            value={html}
            placeholder="Paste here"
            autoFocus
            onChange={(event) => setHtml(event.target.value)}
          />
        </div>

        {error !== undefined && (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}

        <DialogActions>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Read This</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
